import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Typography, Button, Tooltip, message,
  Divider, Result, Tabs,
} from "antd";
import {
  FileExcelOutlined, ReloadOutlined, BarChartOutlined, UnorderedListOutlined,
} from "@ant-design/icons";
import { selectAppPermissions } from "../../store/auth/selectors";
import StatsCards from "./StatsCards";
import TablaUsuarios from "./TablaUsuarios";
import DrawerDetalle from "./DrawerDetalle";
import {
  fetchStats,
  fetchSummary,
  downloadUserTraceExcel,
  downloadExportAllExcel,
} from "../../services/seguimiento/reporteSeguimientoServices";

const { Title, Text } = Typography;
const APP_NAME = "reporte-seguimiento";

const section = {
  background: "#fff",
  borderRadius: 10,
  padding: "20px 24px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
};

const SectionTitle = ({ children }) => (
  <>
    <Text
      type="secondary"
      style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.7px", fontWeight: 600 }}
    >
      {children}
    </Text>
    <Divider style={{ margin: "8px 0 18px" }} />
  </>
);

const ReporteSeguimiento = () => {
  const permissions = useSelector((state) => selectAppPermissions(state, APP_NAME));
  const permissionAccess = permissions.priv_access === "Y";
  const permissionExport = permissions.priv_export === "Y";

  const [stats, setStats] = useState(null);
  const [tableData, setTableData] = useState({ data: [], total: 0 });
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [filters, setFilters] = useState({ page: 1, pageSize: 10 });
  const [drawerUserId, setDrawerUserId] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    const res = await fetchStats();
    if (res.ok) setStats(res.data);
    setLoadingStats(false);
  }, []);

  const loadTable = useCallback(async (f) => {
    setLoadingTable(true);
    const res = await fetchSummary(f);
    if (res.ok) setTableData({ data: res.data, total: res.meta?.total || 0 });
    else message.error(res.message || "Error al cargar usuarios");
    setLoadingTable(false);
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadTable(filters); }, [filters, loadTable]);

  const handleFiltersChange = useCallback(
    (changes) => setFilters((prev) => ({ ...prev, ...changes })),
    []
  );

  const handleExportTrace = useCallback(async (userId, userName) => {
    const key = `dl-${userId}`;
    message.loading({ content: "Generando Excel…", key, duration: 0 });
    const res = await downloadUserTraceExcel(userId, userName);
    if (res.ok) message.success({ content: "Excel descargado correctamente", key });
    else message.error({ content: res.message || "Error al descargar", key });
  }, []);

  const handleExportAll = async () => {
    setExportLoading(true);
    message.loading({ content: "Preparando exportación Excel…", key: "exp-all", duration: 0 });
    const res = await downloadExportAllExcel(filters);
    if (res.ok) message.success({ content: "Excel exportado correctamente", key: "exp-all" });
    else message.error({ content: res.message || "Error al exportar", key: "exp-all" });
    setExportLoading(false);
  };

  // ── Sin permiso de acceso ──────────────────────────────────────────────
  if (!permissionAccess) {
    return (
      <div style={{ padding: "60px 24px", textAlign: "center" }}>
        <Result
          status="403"
          title="Acceso restringido"
          subTitle="No tienes permiso para ver este módulo. Contacta al administrador."
        />
      </div>
    );
  }

  return (
      <div className="container">
        <div className="row mb-2 d-flex align-items-center">
          <div className="col-md-7 linea_separador mb-2 d-flex align-items-center">
            <div className="titulo_proyecto" style={{ flexBasis: '25%', flexGrow: 0 }}>
              <p>PROYECTO UTF <br /> COL 160 COL</p>
            </div>
            <div className="objeto" style={{ flexBasis: '75%', flexGrow: 0 }}>
              <p>
                Implementar actividades de desarrollo económico, derecho humano a la alimentación y fortalecimiento de capacidades
                institucionales, comunitarias y organizacionales que aporten al cierre de brechas y la transformación de los territorios PDET
              </p>
            </div>
          </div>
          <div className="col-md-5 d-flex justify-content-center align-items-center flex-column">
            <h4 className="text-center mb-4">Log de Accesos y Trazabilidad</h4>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Actividad de trazabilidad en todos los módulos
            </Text>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Tooltip title="Actualizar estadísticas y tabla">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => { loadStats(); loadTable(filters); }}
                />
              </Tooltip>
              {permissionExport && (
                <Tooltip title="Exporta todos los usuarios con los filtros aplicados a Excel (2 hojas: resumen + indicadores)">
                  <Button
                    icon={<FileExcelOutlined />}
                    onClick={handleExportAll}
                    loading={exportLoading}
                    style={{ background: "#52c41a", borderColor: "#52c41a", color: "#fff" }}
                  >
                    Exportar Excel
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        <div style={section}>
          <Tabs
            defaultActiveKey="resumen"
            size="small"
            items={[
              {
                key: "resumen",
                label: (
                  <span>
                    <BarChartOutlined style={{ marginRight: 6 }} />
                    Resumen
                  </span>
                ),
                children: (
                  <>
                    <SectionTitle>Resumen general de la plataforma</SectionTitle>
                    <StatsCards stats={stats} loading={loadingStats} />
                  </>
                ),
              },
              {
                key: "usuarios",
                label: (
                  <span>
                    <UnorderedListOutlined style={{ marginRight: 6 }} />
                    Usuarios
                  </span>
                ),
                children: (
                  <>
                    <SectionTitle>Usuarios de la plataforma</SectionTitle>
                    <TablaUsuarios
                      data={tableData.data}
                      total={tableData.total}
                      loading={loadingTable}
                      filters={filters}
                      permissionExport={permissionExport}
                      onFiltersChange={handleFiltersChange}
                      onViewDetail={setDrawerUserId}
                      onExportTrace={handleExportTrace}
                    />
                  </>
                ),
              },
            ]}
          />
        </div>

        <DrawerDetalle
          userId={drawerUserId}
          open={!!drawerUserId}
          permissionExport={permissionExport}
          onClose={() => setDrawerUserId(null)}
          onExportTrace={handleExportTrace}
        />

    </div>
  );
};

export default ReporteSeguimiento;
